-- | Bulk signature modal widget.
-- |
-- | Resolves which signature fields to request for selected Recent
-- | Forms. The Step 1 request-signature mutation is runtime-owned elsewhere;
-- | this module only returns the payloads to send.
module Widgets.Signature.Request where

import Common

import API.Account (AccountInfo)
import API.FormBase (FormID, FormBasic)
import API.RecentForms (RecentForm)
import API.SelStudent (studentName)
import API.Signature (SignatureRequest)
import API.Types (DocumentID)
import Concur.Core.Patterns (loopState)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Array (catMaybes, mapWithIndex, sortWith)
import Data.Array as Arr
import Data.Array.NonEmpty (NonEmptyArray)
import Data.Array.NonEmpty as NE
import Data.Either (either)
import Data.List (List)
import Data.List as List
import Data.Map as Map
import Data.Maybe (isJust, isNothing)
import Data.String (toLower)
import Effect.Console (log)
import GraphQL.Type (innerId, innerStr)
import Internal.ConcurUtil (loopInfinitely)
import Internal.Luxon (LDateTime)
import Internal.Luxon as Luxon
import Internal.Util (justIf)
import Unsafe.Coerce (unsafeCoerce)
import Widgets.Checkbox (checkbox, labelCheckbox')
import Widgets.Confirmation (confirmWidget)
import Widgets.Modal (ModalResult(..), modalWidget)
import Widgets.Signature.Types (FormSigField, SigRelation, SignKey, employerRel, sigRelName)

signatureRequestModal :: AccountInfo -> Array RecentForm -> SigRelation -> Widget HTML (Maybe (Array SignatureRequest))
signatureRequestModal account docs rel = do
    ret <- modalWidget (D.div' []) $ runModal $ targetSigReqs docs rel
    case ret of
        ModalClosed -> pure Nothing
        ModalReturned state -> do
            liftEffect $ log $ unsafeCoerce $ getRequests state
            pure $ Just $ getRequests state
  where
    runModal targ = do
        { targets, rstate } <- case targ of
            Right targets -> pure $ { targets, rstate: Nothing }
            Left toResolve -> resolve toResolve
        review $ (initSigReqState account rel docs targets) { rstate = rstate }

    review :: SigRequestState -> Widget HTML SigRequestState
    review initState = do
        ret <- runReviewList $ initState
        case ret of
            Left _ -> case initState.rstate of
                Just rstate -> runModal $ Left rstate
                Nothing -> runModal $ targetSigReqs docs rel
            Right state -> confirm state

    confirm :: SigRequestState -> Widget HTML SigRequestState
    confirm state = do
        conf <- confirmWidget
            (lookupTranslation "request-signatures")
            (lookupTranslation "request-signatures-confirm-btn")
            (lookupTranslation "request-signatures-confirm")
        if conf
            then pure state
            else review state

    resolve :: AmbiguityResolveState -> Widget HTML
        { targets :: SigReqTargets, rstate :: Maybe AmbiguityResolveState }
    resolve initState = do
       state <- resolveInner initState
       case chosenTargets state of
           Just targets -> pure
               { targets: Map.union targets $ targetsFromUnambig state.others, rstate: Just state }
           Nothing -> resolve state

    resolveInner :: AmbiguityResolveState -> Widget HTML AmbiguityResolveState
    resolveInner state' = loopState state' \state -> D.div
        [ P.className "modal-col" ]
        [ Left <$> renderAmbiguityResolve state
        , D.button
            [ P.className "btn btn-primary"
            , P.disabled $ isNothing $ chosenTargets state
            , Right state <$ P.onClick
            ]
            [ D.text $ lookupTranslation "next" ]
        ]

-- | Mapping from form IDs to the key of fields we're requesting signatures for in that form
type SigReqTargets = Map FormID SignKey

targetsFromUnambig :: Array SigReqUnambiguity -> SigReqTargets
targetsFromUnambig = Map.fromFoldable <<< map process
  where
    process f = Tuple f.form.id f.field.key

-- | Generates the `SigReqTargets` mapping if we can find a single field for
-- | the given relationship in each form provided. If any fields have more than
-- | one, returns `Left` with an `AmbiguityResolveState` instead.
targetSigReqs :: Array RecentForm -> SigRelation -> Either AmbiguityResolveState SigReqTargets
targetSigReqs docs rel = if length ambiguous > 0
    then Left { rel, choices, others: unambiguous }
    else Right targets
  where
    targets :: SigReqTargets
    targets = targetsFromUnambig unambiguous

    choices :: Array SigReqAmbiguity
    choices = Arr.fromFoldable $ map (\{ form, fields } ->
        { opts: fields
        , rel
        , form
        , selected: Nothing
        }) $ ambiguous

    ambiguous :: Array { form :: FormBasic, fields :: NonEmptyArray FormSigField }
    ambiguous = Arr.mapMaybe (\m -> map { form: m.form, fields: _ } $ ambiguousFields m.fields) matched

    ambiguousFields :: Array FormSigField -> Maybe (NonEmptyArray FormSigField)
    ambiguousFields fields | length fields > 1 = NE.fromArray fields
    ambiguousFields _ = Nothing

    unambiguous :: Array SigReqUnambiguity
    unambiguous = Arr.mapMaybe process matched
      where
        process f = { form: f.form, field: _ } <$> unambiguousFields f.fields

    unambiguousFields :: Array FormSigField -> Maybe FormSigField
    unambiguousFields fields | length fields < 2 = Arr.head fields
    unambiguousFields _ = Nothing

    matched :: Array { form :: FormBasic, fields :: Array FormSigField }
    matched = map (\form -> { form, fields: matchingKeys form }) formTypes

    matchingKeys :: FormBasic -> Array FormSigField
    matchingKeys form = Arr.filter (\s -> s.relationship == rel) form.signatures

    formTypes :: Array FormBasic
    formTypes = 
        Arr.nubByEq (\a b -> a.id == b.id) $
        map _.form_type $
        Arr.sortWith _.form_type.id docs

-- Given a list of selected documents and the `SigRelation` we're requesting,
-- build groups and initialize the signature request state.
initSigReqState :: AccountInfo -> SigRelation -> Array RecentForm -> SigReqTargets -> SigRequestState
initSigReqState account rel docs targs =
    { groups, rel, account
    , comment: ""
    , rstate: Nothing
    }
  where
    groups :: Map FormID SigRequestGroup
    groups = Map.fromFoldable $ Arr.catMaybes $ map group grouped

    grouped :: Array (NonEmptyArray RecentForm)
    grouped = Arr.groupBy (\a b -> a.form_type.id == b.form_type.id) $
        Arr.sortWith _.form_type.id docs

    group :: NonEmptyArray RecentForm -> Maybe (Tuple FormID SigRequestGroup)
    group gdocs = justIf (Arr.length docEntryList > 0) $
        Tuple form.id { form, sigs: Map.fromFoldable docEntryList }
      where
        docEntryList = Arr.concatMap docEntries $ NE.toArray gdocs
        form = (NE.head gdocs).form_type

    docEntries :: RecentForm -> Array (Tuple SigReqKey SigRequestEntry)
    docEntries doc = map (\ent -> Tuple ent.key ent) $
        Arr.catMaybes $ map (\field -> docEntry doc field) doc.form_type.signatures

    docEntry :: RecentForm -> FormSigField -> Maybe SigRequestEntry
    docEntry rf field | isTargeted rf.form_type field && not (isSigned rf field) = do
        let defEmail = _.default_email =<< Arr.find (\s -> s.key == field.key) rf.signature_fields
        let email' = if defEmail /= Just "" then defEmail else Nothing
        let verif = find (\v -> v.data.key == field.key) rf.verifications
        let { email, sentAt } = case verif of
                Just v -> { email: Just v.data.email, sentAt: Just v.created_at }
                Nothing -> { email: email', sentAt: Nothing }
        Just { field, sentAt, email
             , selected: isNothing sentAt && isJust email
             , key: { doc: rf.id, sig: field.key }
             , doc: rf
             }
    docEntry _ _ = Nothing

    isTargeted :: FormBasic -> FormSigField -> Boolean
    isTargeted ftype field = maybe false (_ == field.key) $ Map.lookup ftype.id targs

    isSigned :: RecentForm -> FormSigField -> Boolean
    isSigned rf field = any (\s -> s.key == field.key) rf.signatures

isEmpty :: SigRequestState -> Boolean
isEmpty { groups } = Map.isEmpty groups || any isGroupEmpty (Map.values groups)

isGroupEmpty :: SigRequestGroup -> Boolean
isGroupEmpty group = Map.isEmpty group.sigs

type SigRequestGroup =
    { form :: FormBasic
    , sigs :: Map SigReqKey SigRequestEntry
    }

type SigReqKey = { doc :: DocumentID, sig :: SignKey }
type SigRequestEntry =
    { doc :: RecentForm
    , field :: FormSigField
    , email :: Maybe String
    , sentAt :: Maybe LDateTime
    , selected :: Boolean
    , key :: SigReqKey
    }

type SigRequestState =
    { groups :: Map FormID SigRequestGroup
    , comment :: String
    , rel :: SigRelation
    , rstate :: Maybe AmbiguityResolveState
    , account :: AccountInfo
    }

anySelected :: SigRequestState -> Boolean
anySelected { groups } = any inGroup $ Map.values groups
  where
    inGroup :: SigRequestGroup -> Boolean
    inGroup { sigs } = any _.selected $ Map.values sigs

getRequests :: SigRequestState -> Array SignatureRequest
getRequests state = Arr.fromFoldable $ List.concatMap groupReqs $ Map.values state.groups
  where
    groupReqs :: SigRequestGroup -> List SignatureRequest
    groupReqs group = List.catMaybes $ map mkRequest $ Map.values group.sigs

    mkRequest :: SigRequestEntry -> Maybe SignatureRequest
    mkRequest entry@{ email: Just email } | entry.selected = Just
        { docId: entry.doc.id
        , key: entry.field.key
        , email: email
        , comment: state.comment
        }
    mkRequest _ = Nothing

data SigReviewAction = EditEmail FormID SigRequestEntry String | SendEmails | GoBack

runReviewList :: SigRequestState -> Widget HTML (Either Unit SigRequestState)
runReviewList state | isEmpty state = D.div
    [ P.className "modal-col" ]
    [ D.div
        [ P.className "mt-6" ]
        [ D.text $ lookupTranslation "request-all-signed" ]
    ]
runReviewList state = handle =<< D.div
    [ P.className "modal-col modal-wide" ]
    [ renderReviewList state
    , D.div [ P.className "signature-comment-container w-full" ]
        [ D.label
            [ P.htmlFor "comment-input"
            , P.className "signature-comment-label"
            ]
            [ D.text $ lookupTranslation "signature-comment" ]
        , D.textarea
            [ Left <$> state { comment = _ } <$> P.unsafeTargetValue <$> P.onChange
            , P.value state.comment, P._id "comment-input"
            , P.placeholder $ lookupTranslation "signature-comment-placeholder"
            , P.className "widget-input signature-comment"
            ] []
        ]
    , D.div
        [ P.className "modal-buttons" ] $ catMaybes
        [ justIf (isJust state.rstate) $ D.button
            [ P.className "btn"
            , Right GoBack <$ P.onClick
            ]
            [ D.text $ lookupTranslation "back" ]
        , Just $ D.button
            [ P.className "btn btn-primary"
            , P.disabled $ not $ anySelected state
            , Right SendEmails <$ P.onClick
            ]
            [ D.text $ lookupTranslation "send-email" ]
        ]
    ]
  where
    handle :: Either SigRequestState SigReviewAction -> Widget HTML (Either Unit SigRequestState)
    handle (Right act) = handleAction act
    handle (Left state') = runReviewList state'

    handleAction :: SigReviewAction -> Widget HTML (Either Unit SigRequestState)
    handleAction GoBack = pure $ Left unit
    handleAction SendEmails = pure $ Right state
    handleAction (EditEmail formId entry initEmail) = do
        email <- runEmailEdit state.account entry initEmail
        let newEmail = if email /= "" then Just email else Nothing
        let updateEntry = _ { email = newEmail, selected = isJust newEmail }
        let updateGroup group = Just $
                group { sigs = Map.update (Just <<< updateEntry) entry.key group.sigs }
        runReviewList $ state { groups = Map.update updateGroup formId state.groups }

renderReviewList :: SigRequestState -> Widget HTML (Either SigRequestState SigReviewAction)
renderReviewList state = D.div [ P.className "sig-request-review-form" ] $
    [ D.h1 [ P.className "modal-heading" ]
        [ D.text $ "Select " -- FIXME: Translation system doesn't support interpolation
        , D.text $ sigRelName state.rel
        , D.text $ lookupTranslation " signatures to request"
        ]
    ] <>
    (Arr.fromFoldable $ map renderGroup $ Map.values state.groups)
  where
    renderGroup :: SigRequestGroup -> Widget HTML (Either SigRequestState SigReviewAction)
    renderGroup group = either (Left <<< updateGroup) (\a -> Right a) <$>
        renderReviewGroup state group
      where
        updateGroup :: SigRequestGroup -> SigRequestState
        updateGroup newGroup = state { groups = Map.insert group.form.id newGroup state.groups }

renderReviewGroup :: SigRequestState -> SigRequestGroup -> Widget HTML (Either SigRequestGroup SigReviewAction)
renderReviewGroup state group = D.div
    [ P.className "sig-request-group" ]
    [ D.div [ P.className "sig-request-group-header" ]
        [ D.abbr
            [ P.className "form-abbr"
            , P.style { backgroundColor: group.form.bgColor }
            , P.title group.form.label
            ]
            [ D.text $ group.form.abbr ]
        ]
    , D.div [ P.className "sig-request-group-select" ]
        [ sendableGroup
        , noEmailGroup
        , alreadySentGroup
        ]
    ]
  where
    entryTable sent selAll content = D.table [ P.className "sig-request-table" ]
        [ D.thead' $ catMaybes
            [ Just $ D.td' [ fromMaybe (D.text $ lookupTranslation "student") selAll ]
            , justIf (state.rel == employerRel) $
                D.td' [ D.text $ lookupTranslation "employer" ]
            , justIf sent $ D.td' [ D.text $ lookupTranslation "sent" ]
            , Just $ D.td' [ D.text $ lookupTranslation "email" ]
            ]
        , D.tbody' content
        ]

    sendableGroup | length sendableEntries <= 0 = D.div' []
    sendableGroup = D.div [ P.className "sig-request-subgroup" ]
        [ entryTable false (Just $ selectAllCheck false) $ map entryCheck sendableEntries
        ]
    sendableEntries = sortEntries $ Arr.filter (\e -> isNothing e.sentAt && isJust e.email) $ allEntries

    noEmailGroup | length noEmailEntries <= 0 = D.div' []
    noEmailGroup = D.details
        [ P.className "sig-request-subgroup"
        , P.open true
        ]
        [ D.summary' [ D.text $ lookupTranslation "no-saved-email" ]
        , entryTable false Nothing $ map entryCheck noEmailEntries
        ]
    noEmailEntries = sortEntries $ Arr.filter (\e -> isNothing e.email && isNothing e.sentAt) $ allEntries

    alreadySentGroup | length alreadySentEntries <= 0 = D.div' []
    alreadySentGroup = D.details [ P.className "sig-request-subgroup" ]
        [ D.summary' [ D.text $ lookupTranslation "already-emailed" ]
        , entryTable true (Just $ selectAllCheck true) $ map entryCheck alreadySentEntries
        ]
    alreadySentEntries = sortEntries $ Arr.filter (isJust <<< _.sentAt) $ allEntries

    allEntries = Arr.fromFoldable $ Map.values group.sigs

    groupId = "sig-review-" <> innerId group.form.id

    sortEntries = sortWith (toLower <<< _.doc.student.name)

    selectAllCheck :: Boolean -> Widget HTML (Either SigRequestGroup SigReviewAction)
    selectAllCheck alreadySent = Left <$> doSelectAll <$>
        labelCheckbox' "sig-review-check" (groupId <> "-select-all" <> id) (lookupTranslation "student")
            allSelected
      where
        doSelectAll sel = group { sigs = map (\s ->
                s { selected = if matches s then sel && isJust s.email else s.selected }
            ) group.sigs }
        allSelected = all (\s -> s.selected || isNothing s.email || not (matches s)) group.sigs
        matches s | alreadySent = isJust s.sentAt
        matches s = isNothing s.sentAt
        id | alreadySent = "-sent"
        id = "-unsent"

    entryCheck :: SigRequestEntry -> Widget HTML (Either SigRequestGroup SigReviewAction)
    entryCheck entry = D.tr' $ catMaybes
        [ Just $ D.td' [ Left <$> doSelect <$> check ]
        , justIf (entry.field.relationship == employerRel) $
            maybe (D.td' []) (\lbl -> D.td' [ D.text lbl ]) labelEmployer
        , (\when -> D.td' [ D.text when ]) <$> labelWhen
        , Just $ D.td [ P.className "flex flex-row" ] [ D.text labelEmail, editBtn ]
        ]
      where
        check = D.div
            [ P.className "sig-review-check" ]
            [ checkbox [ P._id checkId, P.disabled $ isNothing entry.email ] entry.selected
            , D.label [ P.htmlFor checkId ] [ D.text $ studentName state.account entry.doc.student ]
            ]
        labelEmail = fromMaybe "" entry.email
        labelWhen = Luxon.toRelative <$> entry.sentAt
        labelEmployer = _.name <$> entry.doc.contact
        editBtn = D.button
            [ Right (EditEmail group.form.id entry $ fromMaybe "" entry.email) <$ P.onClick
            , P.className "edit-btn"
            ]
            [ D.span [ P.className "image-replace edit-icon" ] []
            , D.span [ P.className "edit-btn-text" ] [ D.text $ lookupTranslation "edit" ]
            ]
        checkId = groupId <> "-sig-" <> innerId entry.doc.id <> "-" <> innerStr entry.field.key
        doSelect sel = group { sigs = Map.update (\s -> Just $ s { selected = sel }) entry.key group.sigs }


runEmailEdit :: AccountInfo -> SigRequestEntry -> String -> Widget HTML String
runEmailEdit account entry initEmail = modalWidget (D.text "") runModal >>= case _ of
    ModalClosed -> pure initEmail
    ModalReturned email -> pure email
  where
    runModal = loopState initEmail \email -> D.form
        [ P.className "signature-email-single-form signature-req-edit-email modal-grid"
        , P.action "javascript:void(0);"
        , Right email <$ P.onSubmit
        ]
        [ D.h1
            [ P.className "modal-heading" ]
            [ D.text "Edit " -- TODO: Translation interpolation
            , D.text entry.field.emailLabel
            , D.text " for "
            , D.text $ studentName account entry.doc.student
            ]
        , D.div [ P.className "signature-employer-container" ] $ catMaybes
            [ justIf (entry.field.relationship == employerRel) $ D.span [ P.className "signature-employer" ] $
                case entry.doc.contact of 
                    Just contact ->
                        [ D.text contact.name
                        , D.text " at "
                        , D.text $ maybe "no employer selected" _.company entry.doc.employer
                        ]
                    Nothing -> [ D.text $ lookupTranslation "no-contact-selected" ]
            ]
        , D.input
            [ Left <$> P.unsafeTargetValue <$> P.onChange
            , P.value email, P._id "email-input"
            , P._type "email", P.required true
            , P.placeholder $ lookupTranslation "email-address"
            , P.className "widget-input req-email-address"
            ]
        , D.div
            [ P.className "btn-group btn-group-email-single modal-buttons" ]
            [ D.button
                [ P.className "btn btn-primary", P._type "submit" ]
                [ D.text $ lookupTranslation "save" ]
            , D.button
                [ P.className "btn", P._type "button"
                , Right initEmail <$ P.onClick
                ]
                [ D.text $ lookupTranslation "cancel" ]
            ]
        ]


type AmbiguityResolveState =
    { choices :: Array SigReqAmbiguity
    , others :: Array SigReqUnambiguity
    , rel :: SigRelation
    }

type SigReqUnambiguity =
    { form :: FormBasic
    , field :: FormSigField
    }

type SigReqAmbiguity =
    { rel :: SigRelation
    , form :: FormBasic
    , opts :: NonEmptyArray FormSigField
    , selected :: Maybe FormSigField
    }

chosenTargets :: AmbiguityResolveState -> Maybe SigReqTargets
chosenTargets state = justIf (allResolved state) selected
  where
    selected :: SigReqTargets
    selected = Map.fromFoldable $ Arr.catMaybes $
        map (\a -> map (Tuple a.form.id <<< _.key) a.selected) state.choices

allResolved :: AmbiguityResolveState -> Boolean
allResolved state = all (isJust <<< _.selected) state.choices

renderAmbiguityResolve :: AmbiguityResolveState -> Widget HTML AmbiguityResolveState
renderAmbiguityResolve state = D.div [ P.className "sig-request-resolve-form"] $
    [ D.h1 [ P.className "modal-heading" ]
        [ D.text $ "Select " -- FIXME: Translation system doesn't support interpolation
        , D.text $ sigRelName state.rel
        , D.text $ lookupTranslation " signatures to request"
        ]
    ] <>
    (mapWithIndex renderGroup state.choices <> map renderResolveSingle state.others)
  where
    renderGroup :: Int -> SigReqAmbiguity -> Widget HTML AmbiguityResolveState
    renderGroup idx group =
        (\new -> state { choices = fromMaybe state.choices $ Arr.updateAt idx new state.choices })
        <$> renderResolveChoice group

renderResolveChoice :: SigReqAmbiguity -> Widget HTML SigReqAmbiguity
renderResolveChoice group = D.div
    [ P.className "sig-request-group" ]
    [ D.div [ P.className "sig-request-group-header" ]
        [ D.abbr
            [ P.className "form-abbr"
            , P.style { backgroundColor: group.form.bgColor }
            , P.title group.form.label
            ]
            [ D.text $ group.form.abbr ]
        ]
    , D.div [ P.className "sig-request-group-select" ]
        [ D.h1'
            [ D.text $ (lookupTranslation "resolve-signature-for" <> " ")
            , D.b' [ D.text group.form.label ]
            ]
        , D.div' $ NE.toArray $ map radio group.opts
        ]
    ]
  where
    radioName = "sig-request-resolve-" <> innerId group.form.id
    radio :: FormSigField -> Widget HTML SigReqAmbiguity
    radio choice = D.div
        [ P.className "sig-request-resolve-option" ]
        [ D.input
            [ P._type "radio", P.name radioName, P._id htmlId
            , P.checked (map _.key group.selected == Just choice.key)
            , group { selected = Just choice } <$ P.onChange
            ]
        , D.label
            [ P.htmlFor htmlId ]
            [ D.text $ choice.label ]
        ]
      where
        htmlId = radioName <> "-" <> innerStr choice.key

renderResolveSingle :: forall a. SigReqUnambiguity -> Widget HTML a
renderResolveSingle { form, field } = loopInfinitely $ renderResolveChoice
    { form
    , rel: field.relationship
    , opts: NE.singleton field
    , selected: Just field
    }
